import { useEffect, useRef } from "react";

import { ChevronRightIcon } from "@heroicons/react/24/outline";

import classNames from "../../../utils/classNames";

// Slim custom checkbox replacement matching the Stock page — h-4 with primary
// brand fill, no browser-default chrome. Supports indeterminate state.
function MiniCheckbox({
  checked,
  indeterminate,
  onChange,
  disabled,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current && typeof indeterminate === "boolean") {
      ref.current.indeterminate = !checked && indeterminate;
    }
  }, [checked, indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      aria-label={ariaLabel ?? "Select row"}
      className={classNames(
        "h-[16px] w-[16px] cursor-pointer rounded border-slate-300 text-primary-600",
        "focus:ring-1 focus:ring-primary-500 focus:ring-offset-0 focus:outline-none",
        "transition-colors",
        disabled ? "opacity-50 cursor-not-allowed" : ""
      )}
    />
  );
}

// Tightly packed select column: a checkbox + tiny expand chevron with minimal
// padding, freeing horizontal space for content columns. Shared between the
// Stock table and the Awaiting Inventory table so they feel consistent.
export default function SlimSelectCol() {
  return {
    id: "select",
    meta: {
      thClassName: "!px-2 !py-2 !w-12 text-center",
      tdClassName: "!px-2 !py-2 !w-12 align-middle",
    },
    header: ({ table }: any) => (
      <div className="flex items-center justify-center">
        <MiniCheckbox
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
          ariaLabel="Select all rows"
        />
      </div>
    ),
    cell: ({ row }: any) => {
      const isSelected = row.getIsSelected();
      const isExpanded = row.getIsExpanded();

      return (
        <div
          className="flex items-center justify-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <MiniCheckbox
            checked={isSelected}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
            disabled={!row.getCanSelect()}
            ariaLabel={isSelected ? "Deselect row" : "Select row"}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            aria-label={isExpanded ? "Hide details" : "Show details"}
            aria-expanded={isExpanded}
            className={classNames(
              "flex h-5 w-5 items-center justify-center rounded text-slate-400",
              "hover:bg-slate-100 hover:text-slate-700",
              "transition-colors"
            )}
          >
            <ChevronRightIcon
              className={classNames(
                "h-3.5 w-3.5 transition-transform duration-200",
                isExpanded ? "rotate-90" : ""
              )}
            />
          </button>
        </div>
      );
    },
  };
}
